// PurchaseInfo.js
import React, {useEffect, useMemo, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControl,
    List,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import Div from "@jumbo/shared/Div";
import {useAppMutation} from "../../../services";
import adminServices from "../../../services/admin-services";
import {useParentContext} from "./context";
import moment from "moment";
import Select from "@mui/material/Select";
import LoadingButton from "@mui/lab/LoadingButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const fmt = (iso) => (iso ? moment(iso).format("DD/MM/YYYY") : "-");
const isExpiredByDate = (iso) => (iso ? new Date(iso) < new Date() : false);

const PurchaseInfo = () => {
    const {t} = useTranslation();
    const {globalState, setGlobalState} = useParentContext();

    const [planId, setPlanId] = useState("");
    const [parentId, setParentId] = useState("");
    const [disabledDelete, setDisableDelete] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState(-1);

    const {
        mutate: loadSubscriptionsByParent,
        data: subscriptions,
        isLoading: isSubscriptionsLoading,
    } = useAppMutation(adminServices.loadSubscriptionsByParent);

    const {mutate: loadFreePacks, data: freePacks} = useAppMutation(
        adminServices.loadFreePacks,
    );

    const {
        mutate: deleteFreePlan,
        isSuccess: isDFPSuccess,
        isError: isDFPError,
    } = useAppMutation(adminServices.deleteFreePlan);

    const {
        mutate: addFreePacks,
        isLoading: isAFPLoading,
        isSuccess: isAFPSuccess,
    } = useAppMutation(adminServices.addFreePacks);

    const {
        mutate: cancelSubscription,
        isLoading: isCSLoading,
        isSuccess: isCSPSuccess,
    } = useAppMutation(adminServices.cancelSubscription);

    const hide = () => {
        setGlobalState({
            ...globalState,
            openPurchaseInfo: false,
            refreshList: true,
            parent: undefined,
        });
    };

    useEffect(() => {
        if (isDFPSuccess) hide();
        if (isDFPError) setDisableDelete(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDFPSuccess, isDFPError]);

    useEffect(() => {
        if (isAFPSuccess) hide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAFPSuccess]);

    useEffect(() => {
        if (globalState?.parent?.parentId) {
            loadFreePacks();
            loadSubscriptionsByParent(globalState.parent.parentId);
            setParentId(globalState.parent.parentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalState]);

    useEffect(() => {
        if (isCSPSuccess) hide();
    }, [isCSPSuccess]);

    // ✅ Prefer grouped response (same as parent screen)
    const keySubs = useMemo(() => {
        if (Array.isArray(subscriptions?.keySubs)) return subscriptions.keySubs;
        return [];
    }, [subscriptions]);

    // ✅ Fallback to flat list if keySubs not present
    const flatSubs = useMemo(() => {
        if (Array.isArray(subscriptions?.subs)) return subscriptions.subs;
        if (Array.isArray(subscriptions)) return subscriptions;
        return [];
    }, [subscriptions]);

    // If API returned only subs (flat), we can wrap them as single groups to reuse UI
    const groups = useMemo(() => {
        if (keySubs.length) return keySubs;
        return flatSubs.map((s, idx) => ({
            key: `flat-${s?.subscriptionId || idx}`,
            subscription: s,
            subscriptions: [s], // history = itself
        }));
    }, [keySubs, flatSubs]);

    const handleTerminate = (sub) => {
        const payload = {
            subscriptionId: sub?.subscriptionId,
            parentId,
        };

        console.log("🛑 Terminate clicked → payload:", payload);
        console.log("   subscriptionId:", payload.subscriptionId);
        console.log("   parentId:", payload.parentId);

        cancelSubscription(payload);
    };

    return (
        <Box component="form" noValidate autoComplete="off">
            <Div>
                <Div sx={{minWidth: 120}}>
                    {/* ---------------- Add Free Plan ---------------- */}

                    {/*<FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel id="plan-label">
              {t("parent-subscription.title.sub")}
            </InputLabel>
            <Select
              labelId="plan-label"
              value={planId}
              label={t("parent-subscription.title.sub")}
              onChange={(event) => setPlanId(event.target.value)}
              renderValue={(selected) => {
                const selectedChild = freePacks?.find(
                  (child) => child.planId === selected,
                );
                return (
                  <Div sx={{ display: "flex", alignItems: "center" }}>
                    {selectedChild?.name}
                  </Div>
                );
              }}
            >
              {freePacks?.map((item) => (
                <MenuItem key={item.planId} value={item.planId}>
                  <div>
                    {item.name}
                    <br />
                    {t("common.noOfBooking") || "No of Booking"}{" "}
                    {item.noOfBooking}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LoadingButton
            loading={isAFPLoading}
            onClick={() => addFreePacks({ planId, parentId })}
            disabled={!planId}
          >
            Add Free Plan
          </LoadingButton>*/}

                    {/* ---------- Add Free Plan (UI same as screenshot) ---------- */}

                    <Box
                        sx={{
                            px: 0.25,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: "#fff",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "15px",
                                fontWeight: 700,
                                color: "rgb(0, 36, 101)",
                                fontFamily: "Nunito, sans-serif",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                pr: 2,
                            }}
                        >
                            {t("payment-modal.purchaseInfo")}
                            {globalState?.parent
                                ? ` - ${
                                    globalState?.parent?.name ||
                                    globalState?.parent?.fullName ||
                                    globalState?.parent?.firstName ||
                                    globalState?.parent?.parentName ||
                                    ""
                                }`
                                : ""}
                        </Typography>

                        <IconButton
                            aria-label="close"
                            onClick={hide}
                            sx={{
                                color: "rgba(0,0,0,0.55)",
                            }}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    <Divider
                        sx={{
                            width: "100%",
                            borderColor: "#E5E7EB",
                            borderBottomWidth: "1.5px",
                            mx: 0,
                            mb: 2,
                        }}
                    />

                    <Box
                        sx={{
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            p: 1.5,
                            mb: 3,
                            mt: 1,
                        }}
                    >
                        {/* Label */}
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#111827",
                                mb: 0.75,
                                fontFamily: "Nunito, sans-serif",
                            }}
                        >
                            {t("parent-subscription.title.sub") /* or "Plan" */}
                        </Typography>

                        {/* Select */}
                        <FormControl fullWidth size="small">
                            <Select
                                value={planId}
                                onChange={(e) => setPlanId(e.target.value)}
                                displayEmpty
                                sx={{
                                    height: 36,
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    backgroundColor: "#fff",
                                    "& .MuiSelect-select": {
                                        py: 0.75,
                                    },
                                }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return (
                                            <span style={{color: "#6B7280"}}>
                        {t("payment-modal.selectFreePlan")}
                      </span>
                                        );
                                    }
                                    const selectedPack = freePacks?.find(
                                        (p) => p.planId === selected,
                                    );
                                    return selectedPack?.name || "-";
                                }}
                            >
                                {/* placeholder item */}
                                <MenuItem disabled value="">
                                    {t("payment-modal.selectFreePlan")}
                                </MenuItem>

                                {freePacks?.map((item) => (
                                    <MenuItem key={item.planId} value={item.planId}>
                                        <Box>
                                            <Box sx={{fontWeight: 700}}>{item.name}</Box>
                                            <Box sx={{fontSize: 12, color: "rgba(0,0,0,0.65)"}}>
                                                {t("parent-subscription.title.noOfBooking") ||
                                                    "No of Booking"}
                                                : {item.noOfBooking}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Button */}
                        <LoadingButton
                            loading={isAFPLoading}
                            onClick={() => addFreePacks({planId, parentId})}
                            disabled={!planId}
                            fullWidth
                            sx={{
                                mt: 1.25,
                                height: 38,
                                borderRadius: "6px",
                                backgroundColor: "#1D4ED8",
                                color: "#fff",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                fontFamily: "Nunito, sans-serif",
                                "&:hover": {
                                    backgroundColor: "#1E40AF",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(29,78,216,0.35)",
                                    color: "rgba(255,255,255,0.85)",
                                },
                            }}
                        >
                            + {t("payment-modal.addFreePlan")}
                        </LoadingButton>
                    </Box>

                    <List dense sx={{width: "100%", bgcolor: "background.paper"}}>
                        {/* ---------------------- keySubs rendering ---------------------- */}
                        {keySubs.length === 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    borderRadius: "10px",
                                    bgcolor: "#fff",
                                    py: 4,
                                    px: 2,
                                    textAlign: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Nunito, sans-serif",
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        color: "rgb(0, 36, 101)",
                                        mb: 0.5,
                                    }}
                                >
                                    No Subscription for this parent yet
                                </Typography>

                                <Typography
                                    sx={{
                                        fontFamily: "Nunito, sans-serif",
                                        fontSize: "12.5px",
                                        color: "rgba(0,0,0,0.6)",
                                    }}
                                >
                                    Please add free plan above.
                                </Typography>
                            </Paper>
                        ) : (
                            keySubs.map((group, index) => {
                                //console.log("groupData...", group?.subscription);
                                const sub = group?.subscription || {};
                                const plan = sub?.plan || {};
                                const category = plan?.category; // "subscription" | "packs"
                                const isPack = category === "packs";
                                const isSubscription = category === "subscription";
                                const isCancelled = Boolean(sub?.cancelled);

                                const expired = isExpiredByDate(sub?.expiredDate);
                                const hasHistory =
                                    Array.isArray(group?.subscriptions) &&
                                    group.subscriptions.length > 0;

                                const isBoost = Boolean(sub?.boast?.subscription);

                                let stripColor = isPack
                                    ? "rgb(76, 175, 80)" // packs → green
                                    : isSubscription && isBoost
                                        ? "rgb(255, 193, 7)" // boost → yellow
                                        : "rgb(76, 175, 80)"; // normal → green

                                // ✅ highest priority states
                                if (expired || isCancelled) {
                                    stripColor = "rgb(158, 158, 158)";
                                }

                                // ---- PACK UI (requested)
                                const packSubtitle = `Pack ${sub?.noOfBooking ?? 0} ${t(
                                    "parent-subscription.title.course",
                                )} (€${Number(sub?.chargedAmount ?? sub?.amount ?? 0).toFixed(2)})`;

                                const purchaseDate = fmt(sub?.startDate);
                                const expiryDate = fmt(sub?.expiredDate);
                                const lessonsUsed = `${sub?.noOfUsedBooking ?? 0}/${sub?.noOfBooking ?? 0}`;

                                // ---- SUBSCRIPTION subtitle
                                const subtitle = isSubscription
                                    ? t("common.subscriptionTitle")
                                        .replace("?", sub?.noOfBooking)
                                        .concat(
                                            ` (€${Number(sub?.chargedAmount ?? sub?.amount ?? 0).toFixed(2)}/${t(
                                                "parent-subscription.title.month",
                                            )})`,
                                        )
                                    : "";

                                const nextPayment = isSubscription
                                    ? fmt(sub?.nextPaymentDate)
                                    : "-";

                                const isExpanded = expandedIndex === index;

                                const canShowCancelBtn =
                                    Boolean(sub?.allowCancellation) &&
                                    !sub?.cancelled &&
                                    !expired;

                                // ---------- Summary Content (PACK or SUBSCRIPTION) ----------
                                const SummaryContent = (
                                    <Box sx={{width: "100%"}}>
                                        {/* PACK (same for all screens) */}
                                        {isPack ? (
                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography
                                                        sx={{
                                                            color: "rgb(0, 36, 101)",
                                                            fontFamily: "Nunito, sans-serif",
                                                            fontSize: {xs: "16px", sm: "20px"},
                                                            fontWeight: 800,
                                                            lineHeight: 1.2,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {plan?.name || "-"}
                                                    </Typography>
                                                </Stack>

                                                <Typography
                                                    sx={{
                                                        mt: 1.5,
                                                        color: "rgb(102, 102, 102)",
                                                        fontFamily: "Nunito, sans-serif",
                                                        fontSize: {xs: "13px", sm: "16px"},
                                                        display: "block",
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {packSubtitle}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        mt: 1.0,
                                                        fontSize: 12.5,
                                                        color: "rgba(0,0,0,0.78)",
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: "rgb(0, 36, 101)",
                                                            fontFamily: "Nunito, sans-serif",
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            display: "inline",
                                                        }}
                                                    >
                                                        {t("parent-subscription.title.purchaseDate")}
                                                    </Box>{" "}
                                                    {purchaseDate}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        mt: 0.35,
                                                        fontSize: 12.5,
                                                        color: "rgba(0,0,0,0.78)",
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: "rgb(0, 36, 101)",
                                                            fontFamily: "Nunito, sans-serif",
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            display: "inline",
                                                        }}
                                                    >
                                                        {t("parent-subscription.title.expiryDate")}
                                                    </Box>{" "}
                                                    {expiryDate}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        mt: 0.35,
                                                        fontSize: 12.5,
                                                        color: "rgba(0,0,0,0.78)",
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: "rgb(0, 36, 101)",
                                                            fontFamily: "Nunito, sans-serif",
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            display: "inline",
                                                        }}
                                                    >
                                                        {t("parent-subscription.title.lessonUsed")}
                                                    </Box>{" "}
                                                    {lessonsUsed}
                                                </Typography>

                                                {/*<Typography
                            sx={{
                              mt: 0.6,
                              color: "rgb(153, 153, 153)",
                              fontFamily: "Nunito, sans-serif",
                              fontSize: "13px",
                              display: "block",
                              lineHeight: 1.4,
                            }}
                          >
                            ID: {sub?.subscriptionId || sub?._id || "-"}
                          </Typography>*/}
                                            </Box>
                                        ) : null}

                                        {/* SUBSCRIPTION: MOBILE like your screenshot + DESKTOP keep your layout */}
                                        {isSubscription ? (
                                            <>
                                                {/* MOBILE (xs) */}
                                                <Box sx={{display: {xs: "block", sm: "none"}}}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={{
                                                            minWidth: 0,
                                                            flexWrap: "wrap", // ✅ only wraps when needed
                                                            rowGap: 0.6, // ✅ spacing when it wraps to next line
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: "rgb(0, 36, 101)",
                                                                fontFamily: "Nunito, sans-serif",
                                                                fontSize: "15px",
                                                                fontWeight: 800,
                                                                lineHeight: 1.2,
                                                                cursor: "pointer",

                                                                minWidth: 0,
                                                                flex: "1 1 auto", // ✅ name takes available space
                                                                whiteSpace: "nowrap", // ✅ keep in one line
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {plan?.name || "-"}
                                                        </Typography>
                                                    </Stack>

                                                    <Typography
                                                        sx={{
                                                            color: "rgb(102, 102, 102)",
                                                            fontFamily: "Nunito, sans-serif",
                                                            fontSize: "12px",
                                                            lineHeight: 1.4,
                                                            mb: 0.8,
                                                            mt: 1,
                                                        }}
                                                    >
                                                        {subtitle}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11.5,
                                                            color: "rgba(0,0,0,0.78)",
                                                            mb: 0.4,
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "rgb(0, 36, 101)",
                                                                fontFamily: "Nunito, sans-serif",
                                                                fontSize: "11.5px",
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {t("parent-subscription.title.nextPayment")}:
                                                        </Box>{" "}
                                                        {nextPayment}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11.5,
                                                            color: "rgba(0,0,0,0.78)",
                                                            mb: 0.4,
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "rgb(0, 36, 101)",
                                                                fontFamily: "Nunito, sans-serif",
                                                                fontSize: "11.5px",
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {t("parent-subscription.title.subscriptionDate")}:
                                                        </Box>{" "}
                                                        {fmt(sub?.commitmentStartDate)}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11.5,
                                                            color: "rgba(0,0,0,0.78)",
                                                            mb: 0.6,
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "rgb(0, 36, 101)",
                                                                fontFamily: "Nunito, sans-serif",
                                                                fontSize: "11.5px",
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {t("parent-subscription.title.commitmentDate")}:
                                                        </Box>{" "}
                                                        {fmt(sub?.commitmentEndDate)}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11.5,
                                                            color: "rgba(0,0,0,0.65)",
                                                            mb: 1.2,
                                                            wordBreak: "break-all",
                                                        }}
                                                    >
                                                        ID: {sub?.subscriptionId || sub?._id || "-"}
                                                    </Typography>

                                                    {canShowCancelBtn ? (
                                                        <Button
                                                            disabled={expired || isCSLoading}
                                                            onClick={() => handleTerminate(sub)}
                                                            sx={{
                                                                mt: 2,
                                                                height: 35,
                                                                px: "16px",
                                                                borderRadius: "8px",
                                                                backgroundColor: expired
                                                                    ? "rgb(224, 224, 224)"
                                                                    : "rgb(29, 106, 222)",
                                                                color: expired ? "rgb(158, 158, 158)" : "#fff",
                                                                fontFamily: "Nunito, sans-serif",
                                                                fontSize: "13px",
                                                                fontWeight: 700,
                                                                textTransform: "none",
                                                                cursor: expired ? "not-allowed" : "pointer",

                                                                "&:hover": {
                                                                    backgroundColor: expired
                                                                        ? "rgb(224, 224, 224)"
                                                                        : "rgb(30, 64, 175)",
                                                                },

                                                                "&.Mui-disabled": {
                                                                    backgroundColor: "rgb(224, 224, 224)",
                                                                    color: "rgb(158, 158, 158)",
                                                                },
                                                            }}
                                                        >
                                                            {t("parent-subscription.title.terminate")}
                                                        </Button>
                                                    ) : null}
                                                </Box>

                                                {/* DESKTOP (sm+) - keep your existing left+right layout */}
                                                <Box sx={{display: {xs: "none", sm: "block"}}}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "stretch",
                                                        }}
                                                    >
                                                        {/* Left side only */}
                                                        <Box sx={{minWidth: 0}}>
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={1}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        color: "rgb(0, 36, 101)",
                                                                        fontFamily: "Nunito, sans-serif",
                                                                        fontSize: "20px",
                                                                        fontWeight: 800,
                                                                        cursor: "pointer",
                                                                        display: "block",
                                                                        lineHeight: 1.2,
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {plan?.name || "-"}
                                                                </Typography>
                                                            </Stack>

                                                            <Typography
                                                                sx={{
                                                                    mt: 1.5,
                                                                    color: "rgb(102, 102, 102)",
                                                                    fontFamily: "Nunito, sans-serif",
                                                                    fontSize: "16px",
                                                                    display: "block",
                                                                    lineHeight: 1.4,
                                                                }}
                                                            >
                                                                {subtitle}
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    mt: 1,
                                                                    fontSize: 12.5,
                                                                    color: "rgba(0,0,0,0.78)",
                                                                }}
                                                            >
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        color: "rgb(0, 36, 101)",
                                                                        fontFamily: "Nunito, sans-serif",
                                                                        fontSize: "14px",
                                                                        fontWeight: 700,
                                                                        display: "inline",
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    {t("parent-subscription.title.nextPayment")}:
                                                                </Box>{" "}
                                                                {nextPayment}
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    mt: 1,
                                                                    color: "rgb(153, 153, 153)",
                                                                    fontFamily: "Nunito, sans-serif",
                                                                    fontSize: "13px",
                                                                    display: "block",
                                                                    lineHeight: 1.4,
                                                                }}
                                                            >
                                                                {t(
                                                                    "parent-subscription.title.subscriptionDate",
                                                                )}
                                                                : {fmt(sub?.commitmentStartDate)}
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    mt: 0.35,
                                                                    color: "rgb(153, 153, 153)",
                                                                    fontFamily: "Nunito, sans-serif",
                                                                    fontSize: "13px",
                                                                    display: "block",
                                                                    lineHeight: 1.4,
                                                                }}
                                                            >
                                                                {t("parent-subscription.title.commitmentDate")}:{" "}
                                                                {fmt(sub?.commitmentEndDate)}
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    mt: 0.35,
                                                                    color: "rgb(153, 153, 153)",
                                                                    fontFamily: "Nunito, sans-serif",
                                                                    fontSize: "13px",
                                                                    display: "block",
                                                                    lineHeight: 1.4,
                                                                }}
                                                            >
                                                                ID: {sub?.subscriptionId || sub?._id || "-"}
                                                            </Typography>

                                                            {canShowCancelBtn ? (
                                                                <Button
                                                                    disabled={
                                                                        expired || isCancelled || isCSLoading
                                                                    }
                                                                    onClick={() => handleTerminate(sub)}
                                                                    sx={{
                                                                        mt: 2,
                                                                        height: 35,
                                                                        px: "16px",
                                                                        borderRadius: "8px",
                                                                        backgroundColor:
                                                                            expired || isCancelled
                                                                                ? "rgb(224, 224, 224)"
                                                                                : "rgb(29, 106, 222)",
                                                                        color:
                                                                            expired || isCancelled
                                                                                ? "rgb(158, 158, 158)"
                                                                                : "#fff",
                                                                        fontFamily: "Nunito, sans-serif",
                                                                        fontSize: "13px",
                                                                        fontWeight: 700,
                                                                        textTransform: "none",
                                                                        cursor:
                                                                            expired || isCancelled
                                                                                ? "not-allowed"
                                                                                : "pointer",

                                                                        "&:hover": {
                                                                            backgroundColor:
                                                                                expired || isCancelled
                                                                                    ? "rgb(224, 224, 224)"
                                                                                    : "rgb(30, 64, 175)",
                                                                        },

                                                                        "&.Mui-disabled": {
                                                                            backgroundColor: "rgb(224, 224, 224)",
                                                                            color: "rgb(158, 158, 158)",
                                                                        },
                                                                    }}
                                                                >
                                                                    {isCSLoading
                                                                        ? t("common.loading")
                                                                        : isCancelled
                                                                            ? t(
                                                                            "parent-subscription.title.cancelled",
                                                                        ) || "Cancelled"
                                                                            : t(
                                                                                "parent-subscription.title.terminate",
                                                                            )}
                                                                </Button>
                                                            ) : null}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </>
                                        ) : null}
                                    </Box>
                                );

                                // ---------- PACK: NO ACCORDION / NO EXPAND ICON ----------
                                if (isPack || !hasHistory) {
                                    return (
                                        <Paper
                                            key={group?.key || `key-sub-${index}`}
                                            elevation={0}
                                            sx={{
                                                position: "relative",
                                                borderRadius: "10px",
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                overflow: "hidden",
                                                bgcolor: "#FFFFFF",
                                                mb: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: 6,
                                                    bgcolor: stripColor,
                                                }}
                                            />
                                            <Box sx={{px: 2, py: 1.8, pl: 3}}>{SummaryContent}</Box>
                                        </Paper>
                                    );
                                }

                                // ---------- SUBSCRIPTION: ACCORDION WITH HISTORY ----------
                                return (
                                    <Paper
                                        key={group?.key || `key-sub-${index}`}
                                        elevation={0}
                                        sx={{
                                            position: "relative",
                                            borderRadius: "10px",
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            overflow: "hidden",
                                            bgcolor: "#FFFFFF",
                                            mb: 2,
                                        }}
                                    >
                                        <Accordion
                                            expanded={isExpanded}
                                            onChange={() => setExpandedIndex(isExpanded ? -1 : index)}
                                            disableGutters
                                            elevation={0}
                                            sx={{
                                                // ✅ boost highlight background
                                                background: isBoost
                                                    ? "linear-gradient(90deg, rgb(255, 249, 230) 0%, rgba(255, 249, 230, 0) 100%)"
                                                    : "transparent",

                                                // keep your existing styling
                                                "&:before": {display: "none"},

                                                // optional: ensure it doesn’t add default background/box
                                                boxShadow: "none",
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    <PlayArrowIcon
                                                        sx={{fontSize: 16, color: "rgba(0,0,0,0.55)"}}
                                                    />
                                                }
                                                sx={{
                                                    position: "relative",
                                                    px: 2,
                                                    py: {xs: 1.4, sm: 1.8},
                                                    minHeight: {xs: 74, sm: 88},
                                                    pl: 3,
                                                    "& .MuiAccordionSummary-content": {
                                                        my: 0,
                                                        alignItems: "stretch",
                                                    },
                                                    "& .MuiAccordionSummary-expandIconWrapper": {
                                                        order: -1,
                                                        mr: 1.2,
                                                        ml: 0,
                                                        alignSelf: "flex-start",
                                                        marginTop: {xs: "3px", sm: "2px"},
                                                        transform: isExpanded
                                                            ? "rotate(90deg)"
                                                            : "rotate(0deg)",
                                                        transition: "transform .18s ease",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: 6,
                                                        bgcolor: stripColor,
                                                    }}
                                                />
                                                {SummaryContent}
                                            </AccordionSummary>

                                            <AccordionDetails
                                                sx={{
                                                    px: 2,
                                                    pt: 2,
                                                    pb: 2,
                                                    backgroundColor: "#FFF",
                                                }}
                                            >
                                                <Stack spacing={2} sx={{pl: 3.2}}>
                                                    {group.subscriptions.map((h, i) => {

                                                        console.log("HData----", h);
                                                        //const isBoost = Boolean(h?.boast?.subscription) && i===0;
                                                        const result = (() => {
                                                            const subs = group?.subscriptions.filter(item => !!item?.boast?.subscription) || [];

                                                            // Step 1: check if any item has boast.subscription
                                                            console.log('subs.length', subs.length)
                                                            if (subs.length < 1) return null;

                                                            // Step 2: return second last
                                                            return subs[subs.length - 1];
                                                        })();

                                                        // const isBoost = result?.subscriptionId === h.subscriptionId

                                                        const isBoost =  sub?.boast?.subscription === h.subscriptionId
                                                        const isBoostDate = sub?.boast?.startDate

                                                        const paid = Boolean(h?.paid);

                                                        const bg = !paid
                                                            ? "#FBE7EA" // unpaid / failed
                                                            : isBoost
                                                                ? "rgb(255, 249, 230)" // ⚡ boost
                                                                : "#FFFFFF"; // normal paid

                                                        const totalBookings = Number(h?.noOfBooking ?? 0);
                                                        const usedBookings = Number(
                                                            h?.noOfUsedBooking ?? 0,
                                                        );

                                                        const expired = isExpiredByDate(h?.expiredDate);

                                                        const allUsed =
                                                            paid &&
                                                            totalBookings > 0 &&
                                                            usedBookings >= totalBookings;

                                                        const stripOld = !paid
                                                            ? "#FF1E1E" // failed / unpaid (highest priority)
                                                            : isBoost
                                                                ? "rgb(255, 193, 7)" // ⚡ boost → yellow
                                                                : allUsed
                                                                    ? "rgb(224, 224, 224)" // paid but fully used
                                                                    : "rgb(76,175,80)"; // paid & active

                                                        const strip = !paid
                                                            ? "#FF1E1E" // failed / unpaid
                                                            : expired || allUsed
                                                                ? "rgb(224, 224, 224)" // expired or fully used
                                                                : isBoost
                                                                    ? "rgb(255, 193, 7)" // boost
                                                                    : "rgb(76,175,80)"; // paid & active

                                                        //const strip = paid ? "rgb(76,175,80)" : "#FF1E1E";
                                                        const statusColor = paid
                                                            ? "rgb(76,175,80)"
                                                            : "rgb(244,67,54)";
                                                        const titleIcon = paid ? "✓" : "✖";

                                                        const historyAmount = `€${Number(
                                                            h?.chargedAmount ?? h?.amount ?? 0,
                                                        ).toFixed(2)}`;

                                                        const historyLessons =
                                                            typeof h?.noOfBooking !== "undefined"
                                                                ? `${h?.noOfUsedBooking ?? 0}/${h?.noOfBooking ?? 0}`
                                                                : null;

                                                        return (
                                                            <Paper
                                                                key={i}
                                                                elevation={0}
                                                                sx={{
                                                                    position: "relative",
                                                                    borderRadius: "10px",
                                                                    border: "1px solid rgba(0,0,0,0.08)",
                                                                    overflow: "hidden",
                                                                    bgcolor: bg,
                                                                    px: 3,
                                                                    py: 2,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        left: 0,
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        width: 3,
                                                                        bgcolor: strip,
                                                                    }}
                                                                />

                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "flex-start",
                                                                        justifyContent: "space-between",
                                                                        gap: 2,
                                                                        flexDirection: {
                                                                            xs: "column",
                                                                            sm: "row",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Box sx={{width: "100%"}}>
                                                                        {!isBoost ? (
                                                                            <Typography
                                                                                sx={{
                                                                                    color: "rgb(0, 36, 101)",
                                                                                    display: "block",
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "16px",
                                                                                    fontWeight: 700,
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                {t(
                                                                                    "parent-subscription.title.paymentOn",
                                                                                )}{" "}
                                                                                {fmt(h?.startDate)}
                                                                            </Typography>
                                                                        ) : (
                                                                            <><Typography
                                                                                sx={{
                                                                                    color: "rgb(0, 36, 101)",
                                                                                    display: "block",
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "16px",
                                                                                    fontWeight: 700,
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                ⚡{" "}
                                                                                {t(
                                                                                    "parent-subscription.title.boostActiveOn",
                                                                                )}{" "}
                                                                                {fmt(isBoostDate)}
                                                                            </Typography><Typography
                                                                                sx={{
                                                                                    color: "rgb(0, 36, 101)",
                                                                                    display: "block",
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "16px",
                                                                                    fontWeight: 700,
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                {t(
                                                                                    "parent-subscription.title.paymentOn",
                                                                                )}{" "}
                                                                                {fmt(h?.startDate)}
                                                                            </Typography></>
                                                                        )}
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 12.5,
                                                                                color: "#111",
                                                                                mb: 0.8,
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: "rgb(0, 36, 101)",
                                                                                    display: "inline",
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "13px",
                                                                                    fontWeight: 700,
                                                                                }}
                                                                            >
                                                                                {t("parent-subscription.title.amount")}:
                                                                            </Box>{" "}
                                                                            {historyAmount}
                                                                        </Typography>

                                                                        <Typography
                                                                            sx={{mb: historyLessons ? 0.8 : 0}}
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: statusColor,
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {titleIcon}
                                                                            </Box>{" "}
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: statusColor,
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "13px",
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {t("parent-subscription.title.status")}:
                                                                            </Box>{" "}
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: statusColor,
                                                                                    fontFamily: "Nunito, sans-serif",
                                                                                    fontSize: "13px",
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {paid
                                                                                    ? t("parent-subscription.title.paid")
                                                                                    : t(
                                                                                        "parent-subscription.title.failed",
                                                                                    )}
                                                                            </Box>
                                                                        </Typography>

                                                                        {historyLessons ? (
                                                                            <Typography>
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        color: "rgb(0, 36, 101)",
                                                                                        display: "inline",
                                                                                        fontFamily: "Nunito, sans-serif",
                                                                                        fontSize: "13px",
                                                                                        fontWeight: 700,
                                                                                    }}
                                                                                >
                                                                                    {t(
                                                                                        "parent-subscription.title.lessonUsed",
                                                                                    )}
                                                                                    :
                                                                                </Box>{" "}
                                                                                {historyLessons}
                                                                            </Typography>
                                                                        ) : null}
                                                                    </Box>

                                                                    {/*!paid ? (
                                    <Button
                                      variant="contained"
                                      disableElevation
                                      fullWidth={true}
                                      sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontFamily: "Nunito, sans-serif",
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        px: 3,
                                        height: 36,
                                        bgcolor: "rgb(244,67,54)",
                                        "&:hover": { bgcolor: "#DC2626" },
                                        width: { xs: "100%", sm: "auto" },
                                        alignSelf: {
                                          xs: "stretch",
                                          sm: "flex-start",
                                        },
                                      }}
                                    >
                                      {t("parent-subscription.title.pay")}
                                    </Button>
                                  ) : null*/}
                                                                </Box>
                                                            </Paper>
                                                        );
                                                    })}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Paper>
                                );
                            })
                        )}
                    </List>
                </Div>
            </Div>

            {/*<Div
        sx={{
          display: "flex",
          flexDirection: "row",
          pt: 2,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button onClick={hide} sx={{ mr: 1 }}>
          {t("common.closeBtn")}
        </Button>
      </Div>*/}
        </Box>
    );
};

export default PurchaseInfo;
